
import { Vue, Component, Watch, Ref } from "vue-property-decorator";
import Ace from "@/components/helpers/ace.vue";
import beautify from "js-beautify";
import editor from "./editor";
import appConfigModule from "@/store/plugins/appConfig";

function insertHtmlAtCursor(html: string, target: HTMLElement) {
    if (!target || !target.isConnected) return;
    target.focus();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const fragment = range.createContextualFragment(html);
    const lastChild = fragment.lastChild;

    range.insertNode(fragment);

    if (lastChild) {
        range.setStartAfter(lastChild);
    } else {
        range.setStartAfter(range.startContainer);
    }

    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);
}

@Component({
    mixins: [editor],
    components: { Ace }
})
export default class HmtlInlineEditor extends Vue {
    dialog: boolean = false;
    mode: number = 0;
    modeList: string[] = ["WYSIWYG", "HTML", "Просмотр"];
    save: any;
    cancelChanges: any;
    $confirm: any;
    loading: boolean = false;

    content: string = "";
    htmlValue: string = "";
    value: string = "";

    showLinkModal: boolean = false;
    linkUrl: string = "";
    linkText: string = "";

    showImageModal: boolean = false;
    imageUrl: string = "";
    imageAlt: string = "";

    showTableModal: boolean = false;
    tableRows: number = 3;
    tableCols: number = 3;
    tableHasHeader: boolean = true;

    formatMenu: boolean = false;

    savedSelectionText: string = "";

    // Отступы блока под курсором
    selectedBlockEl: any = null;
    selectedBlockLabel: string = "";
    spacingSides = [
        { key: "Top", label: "↑" },
        { key: "Bottom", label: "↓" },
        { key: "Left", label: "←" },
        { key: "Right", label: "→" },
    ];
    blockSpacing: any = {
        marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0,
        paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0,
    };

    // Текст, с которым открыли редактор: по нему понимаем, есть ли несохранённое
    openedWith: string = "";

    // История правок: снимки текста и положения курсора
    historyLimit: number = 50;
    history: Array<{ html: string; caret: number }> = [];
    historyIndex: number = -1;
    historyTimer: any = null;
    historyPending: boolean = false;

    activeFormats = {
        bold: false,
        italic: false,
        underline: false,
    };

    // === Цвет и размер шрифта ===
    currentColor: string = "#000000";
    colors = [
        // Базовые
        '#000000', // Black
        '#ffffff', // White
        '#ff0000', // Red
        '#00ff00', // Lime (Green)
        '#0000ff', // Blue

        // Основные цвета (CSS)
        '#ffff00', // Yellow
        '#00ffff', // Cyan
        '#ff00ff', // Magenta
        '#ffa500', // Orange
        '#800080', // Purple

        // Оттенки серого
        '#808080', // Gray
        '#c0c0c0', // Silver
        '#a9a9a9', // Dark Gray
        '#d3d3d3', // Light Gray
        '#696969', // Dim Gray

        // Пастельные и мягкие тона
        '#ff6347', // Tomato
        '#ffa07a', // Light Salmon
        '#98fb98', // Pale Green
        '#87ceeb', // Sky Blue
        '#dda0dd', // Plum

        // Яркие/насыщенные
        '#ff1493', // Deep Pink
        '#00ced1', // Dark Turquoise
        '#ff8c00', // Dark Orange
        '#32cd32', // Lime Green
        '#4b0082', // Indigo

        // Дополнительные популярные
        '#8a2be2', // Blue Violet
        '#ff69b4', // Hot Pink
        '#20b2aa', // Light Sea Green
        '#7fff00', // Chartreuse
        '#dc143c', // Crimson

        // Нейтральные и earth tones
        '#cd853f', // Peru
        '#deb887', // Burlywood
        '#556b2f', // Dark Olive Green
        '#8b4513', // Saddle Brown
        '#2f4f4f', // Dark Slate Gray
        '#483d8b'  // Dark Slate Blue
    ];
    // Размер задаём в пикселях. Раньше тут были размеры 1–7 из старого HTML:
    // браузер рисовал их по своей шкале (например, «24» показывался как 32 px),
    // а при сохранении получалось третье число — в редакторе и на сайте
    // размер не совпадал.
    fontSizes = [
        { text: "8", value: "8px" },
        { text: "10", value: "10px" },
        { text: "12", value: "12px" },
        { text: "14", value: "14px" },
        { text: "16", value: "16px" },
        { text: "18", value: "18px" },
        { text: "20", value: "20px" },
        { text: "24", value: "24px" },
        { text: "28", value: "28px" },
        { text: "36", value: "36px" },
    ];
    selectedFontSize: string = "16px";

    // === ЕДИНСТВЕННОЕ место для хранения выделения ===
    private savedRange: Range | null = null;

    @Ref("editor") editorRef!: HTMLElement;

    @Watch("dialog")
    onDialogChange(val: boolean) {
        if (val) {
            this.content = this.value || "<p><br></p>";
            this.htmlValue = this.format(this.content);
            this.openedWith = this.htmlValue;
            this.selectedBlockEl = null;
            this.selectedBlockLabel = "";
            this.initHistory();
            // Enter должен создавать абзац. По умолчанию браузер вставляет <div>,
            // и разметка выходила смешанной: часть блоков с отступами на сайте, часть без.
            try {
                document.execCommand("defaultParagraphSeparator", false, "p");
            } catch (e) {
                console.warn("Не удалось задать разделитель абзацев", e);
            }
            this.$nextTick(() => {
                if (this.mode === 0 && this.editorRef) {
                    this.editorRef.innerHTML = this.content;
                    this.updateActiveFormats();
                }
            });
        } else {
            this.value = this.format(this.content);
        }
    }

    onInput() {
        if (this.mode === 0) {
            this.normalizeEmptyEditor();
            this.content = this.editorRef.innerHTML;
            this.scheduleHistory();
        }
    }

    /**
     * Если выделить весь текст и удалить, браузер оставляет пустые <b>, <span>
     * и «залипшее» состояние жирного — новый текст набирался жирным. Приводим
     * пустой редактор к чистому абзацу и снимаем оформление.
     */
    normalizeEmptyEditor() {
        const el = this.editorRef;
        if (!el) return;
        const text = (el.textContent || "").replace(/​/g, "").trim();
        if (text) return;
        if (el.querySelector("img, iframe, table")) return;

        // Разметку переписываем только если она отличается, а оформление
        // снимаем всегда: после удаления всего текста браузер уже оставляет
        // <p><br></p>, но помнит, что набирать нужно жирным.
        if (el.innerHTML !== "<p><br></p>") el.innerHTML = "<p><br></p>";
        const paragraph = el.firstElementChild;
        if (!paragraph) return;

        const range = document.createRange();
        range.setStart(paragraph, 0);
        range.collapse(true);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        try {
            document.execCommand("removeFormat");
            ["bold", "italic", "underline"].forEach((command) => {
                if (document.queryCommandState(command)) document.execCommand(command);
            });
        } catch (e) {
            console.warn("Не удалось снять оформление в пустом редакторе", e);
        }
    }

    // ── Предпросмотр: текст в стилях своего сайта ──────────────────────────────
    // Стили сайта грузятся ВНУТРЬ рамки: на интерфейс админки они не влияют,
    // у каждого проекта подтягиваются свои (адрес — из конфига проекта).

    get previewSiteUrl(): string {
        const fromConfig = String(appConfigModule.config?.siteUrl ?? "").trim();
        if (fromConfig) return fromConfig.replace(/\/+$/, "");
        // запасной вариант: адрес админки без префикса manage. (manage.lorlor.ru → lorlor.ru)
        const host = window.location.hostname.replace(/^manage\./i, "");
        return `${window.location.protocol}//${host}`;
    }

    get previewCssLinks(): string[] {
        const configured = appConfigModule.config?.contentCss;
        const list: string[] = Array.isArray(configured) && configured.length
            ? configured
            : ["/styles/css/main.css"];
        return list.map((href: string) =>
            /^https?:/i.test(href) ? href : `${this.previewSiteUrl}${href.startsWith("/") ? "" : "/"}${href}`
        );
    }

    get previewSrcDoc(): string {
        const site = this.previewSiteUrl;
        const links = this.previewCssLinks
            .map((href) => `<link rel="stylesheet" href="${href}">`)
            .join("\n");
        // Обёртки те же, что на странице материала: article → .ql-editor → .full-info,
        // ширина колонки как на сайте — иначе переносы строк не совпадут.
        return `<!doctype html>
<html lang="ru">
<head>
<meta charset="utf-8">
<base href="${site}/">
${links}
<style>
  body { margin: 0; background: #fff; }
  /* ширина колонки материала на сайте (~883px): иначе переносы строк не совпадут */
  .preview-page { max-width: 883px; margin: 0 auto; padding: 16px 0 48px; }
</style>
</head>
<body>
<div class="preview-page">
  <article>
    <div class="ql-editor">
      <div class="full-info">${this.content || ""}</div>
    </div>
  </article>
</div>
</body>
</html>`;
    }

    // Вставка из чатов (Qwen, ChatGPT), Word и Google Docs приносит чужую разметку:
    // абзацы как <div class="...-paragraph"> с зашитыми font-size/color, пустые div-распорки,
    // обёртки <span>. В редакторе они выглядят одним сплошным текстом, на сайте — иначе,
    // и абзацы теряются при сохранении. Поэтому вставляем уже очищенный HTML.
    onPaste(e: ClipboardEvent) {
        const clipboard = e.clipboardData;
        if (!clipboard) return;
        const html = clipboard.getData("text/html");
        if (!html) return; // обычный текст — стандартная вставка браузера
        e.preventDefault();
        const clean = this.sanitizePastedHtml(html);
        if (!clean) return;
        insertHtmlAtCursor(clean, this.editorRef);
        this.syncContent();
        this.updateActiveFormats();
    }

    sanitizePastedHtml(html: string): string {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const body = doc.body;
        if (!body) return "";

        body.querySelectorAll("script, style, meta, link, noscript").forEach((n) => n.remove());

        // Теги, которые оставляем как есть
        const keepTags = new Set([
            "p", "h1", "h2", "h3", "h4", "h5", "h6",
            "ul", "ol", "li", "br", "a", "b", "strong", "i", "em", "u",
            "blockquote", "img", "table", "thead", "tbody", "tr", "td", "th"
        ]);
        // Обёртки без смысла — разворачиваем, оставляя содержимое
        const unwrapTags = new Set(["span", "font", "section", "article", "main", "header", "footer", "figure", "figcaption", "small", "mark"]);
        const keepAttrs: Record<string, string[]> = { a: ["href", "target", "rel"], img: ["src", "alt"] };

        const unwrap = (el: Element) => {
            const parent = el.parentNode;
            if (!parent) return;
            while (el.firstChild) parent.insertBefore(el.firstChild, el);
            parent.removeChild(el);
        };

        const isBlock = (el: Element) =>
            ["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "table", "blockquote"].includes(el.tagName.toLowerCase());

        // Обходим снизу вверх: сначала вложенные узлы
        const all = Array.from(body.querySelectorAll("*")).reverse();
        for (const el of all) {
            const tag = el.tagName.toLowerCase();

            if (tag === "div") {
                // div-абзац → p, div-обёртка вокруг блоков → разворачиваем,
                // пустая распорка → удаляем
                const hasBlockChild = Array.from(el.children).some(isBlock);
                if (hasBlockChild) {
                    unwrap(el);
                } else if (!(el.textContent || "").trim() && !el.querySelector("img, br")) {
                    el.remove();
                } else {
                    const p = doc.createElement("p");
                    while (el.firstChild) p.appendChild(el.firstChild);
                    el.parentNode?.replaceChild(p, el);
                }
                continue;
            }

            if (unwrapTags.has(tag)) {
                unwrap(el);
                continue;
            }

            if (!keepTags.has(tag)) {
                unwrap(el);
                continue;
            }

            // Чужие класс/стиль/data-атрибуты убираем, у ссылок и картинок оставляем нужное
            const allowed = keepAttrs[tag] ?? [];
            for (const attr of Array.from(el.attributes)) {
                if (!allowed.includes(attr.name.toLowerCase())) el.removeAttribute(attr.name);
            }
        }

        // Пустые абзацы, оставшиеся после чистки
        body.querySelectorAll("p").forEach((p) => {
            if (!(p.textContent || "").trim() && !p.querySelector("img, br")) p.remove();
        });

        return body.innerHTML.replace(/&nbsp;/g, " ").trim();
    }

    onHtmlInput(value: string) {
        this.htmlValue = value;
    }

    onTabChange() {
        if (this.mode === 1) {
            this.htmlValue = this.format(this.content);
        } else if (this.mode === 0) {
            this.content = this.htmlValue;
            this.$nextTick(() => {
                if (this.editorRef) {
                    this.editorRef.innerHTML = this.content;
                    // правка во вкладке HTML — тоже шаг истории
                    this.pushHistory();
                    this.updateActiveFormats();
                }
            });
        }
    }

    // Команды, которые действуют на блок целиком: перед ними подрезаем выделение,
    // иначе выравнивание или список уезжают на соседний абзац
    static readonly BLOCK_COMMANDS = [
        "justifyLeft", "justifyCenter", "justifyRight", "justifyFull",
        "insertUnorderedList", "insertOrderedList", "formatBlock",
    ];

    execCmd(command: string, value?: string) {
        this.restoreSelection();
        this.editorRef?.focus();
        if (HmtlInlineEditor.BLOCK_COMMANDS.includes(command)) this.trimSelectionEnd();
        document.execCommand(command, false, value);
        this.syncContent();
        this.updateActiveFormats();
    }

    /**
     * Абзац / заголовок / цитата отдельной командой: у заголовка дополнительно
     * снимаем размер шрифта внутри. Иначе оставшийся <font size> или
     * <span style="font-size"> перебивает размер заголовка, и на экране
     * ничего не меняется — «заголовок не применяется».
     */
    applyBlockFormat(tag: string) {
        this.formatMenu = false;
        this.restoreSelection();
        this.editorRef?.focus();
        this.trimSelectionEnd();
        document.execCommand("styleWithCSS", false, "false");
        document.execCommand("formatBlock", false, tag);
        if (/^h[1-6]$/i.test(tag)) this.clearFontSizeInHeadings();
        this.syncContent();
        this.updateActiveFormats();
    }

    /** Убирает размер шрифта в заголовках, которых коснулось выделение */
    clearFontSizeInHeadings() {
        const el = this.editorRef;
        const selection = window.getSelection();
        if (!el || !selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);

        el.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading: any) => {
            if (!range.intersectsNode(heading)) return;
            heading.style.removeProperty("font-size");
            if (!heading.getAttribute("style")) heading.removeAttribute("style");

            heading.querySelectorAll('[style*="font-size"], font[size]').forEach((node: any) => {
                node.removeAttribute("size");
                node.style.removeProperty("font-size");
                if (!node.getAttribute("style")) node.removeAttribute("style");
                const tagName = node.tagName.toLowerCase();
                if ((tagName === "span" || tagName === "font") && node.attributes.length === 0) {
                    const parent = node.parentNode;
                    if (!parent) return;
                    while (node.firstChild) parent.insertBefore(node.firstChild, node);
                    parent.removeChild(node);
                }
            });
        });
    }

    /**
     * Выделяя строку мышью, легко зацепить самое начало следующего абзаца —
     * видимой разницы нет, а команда применяется и к нему тоже. Если конец
     * выделения стоит в начале узла, отводим его к концу предыдущего текста.
     */
    trimSelectionEnd() {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
        if (!this.editorRef) return;

        const range = selection.getRangeAt(0);
        if (!this.editorRef.contains(range.commonAncestorContainer)) return;
        if (range.endOffset !== 0) return;

        const walker = document.createTreeWalker(this.editorRef, NodeFilter.SHOW_TEXT, null as any);
        let previous: any = null;
        let current: any;
        while ((current = walker.nextNode())) {
            if (current === range.endContainer) break;
            if (range.endContainer.nodeType === Node.ELEMENT_NODE &&
                range.endContainer.contains(current)) break;
            if (current.data.trim()) previous = current;
        }
        if (!previous) return;

        range.setEnd(previous, previous.data.length);
        selection.removeAllRanges();
        selection.addRange(range);
        this.savedRange = range.cloneRange();
    }

    // ── История правок ────────────────────────────────────────────────────────
    // Своя история вместо браузерной: браузер не сообщает, сколько шагов он
    // помнит, поэтому показать это на кнопке было нельзя. Храним снимки текста
    // вместе с положением курсора.

    get undoCount(): number {
        // historyPending — набранное только что, снимок которого ещё не записан:
        // без него подсказка показывала «0 шагов», хотя отмена срабатывала
        return Math.max(0, this.historyIndex + (this.historyPending ? 1 : 0));
    }

    get redoCount(): number {
        return Math.max(0, this.history.length - 1 - this.historyIndex);
    }

    initHistory() {
        this.history = [{ html: this.content, caret: 0 }];
        this.historyIndex = 0;
        this.historyPending = false;
    }

    /** Снимок текущего состояния. Одинаковые подряд не сохраняем */
    pushHistory() {
        if (!this.editorRef) return;
        const html = this.editorRef.innerHTML;
        this.historyPending = false;
        if (this.history[this.historyIndex]?.html === html) return;

        this.history = this.history.slice(0, this.historyIndex + 1);
        this.history.push({ html, caret: this.getCaretOffset() });
        if (this.history.length > this.historyLimit) this.history.shift();
        this.historyIndex = this.history.length - 1;
    }

    /** При наборе текста снимок делаем с задержкой, чтобы шаг был не на каждую букву */
    scheduleHistory() {
        this.historyPending = !!this.editorRef &&
            this.history[this.historyIndex]?.html !== this.editorRef.innerHTML;
        clearTimeout(this.historyTimer);
        this.historyTimer = setTimeout(() => this.pushHistory(), 500);
    }

    /** Записать состояние сразу — после команд панели */
    syncContent() {
        if (!this.editorRef) return;
        this.content = this.editorRef.innerHTML;
        clearTimeout(this.historyTimer);
        this.pushHistory();
    }

    runHistoryCommand(command: "undo" | "redo") {
        clearTimeout(this.historyTimer);
        this.pushHistory();

        if (command === "undo") {
            if (this.historyIndex <= 0) return;
            this.historyIndex--;
        } else {
            if (this.historyIndex >= this.history.length - 1) return;
            this.historyIndex++;
        }

        const state = this.history[this.historyIndex];
        if (!state || !this.editorRef) return;
        this.editorRef.innerHTML = state.html;
        this.content = state.html;
        this.editorRef.focus();
        this.setCaretOffset(state.caret);
        this.updateActiveFormats();
    }

    /** Ctrl+Z и Ctrl+Shift+Z ведём через свою историю, иначе счётчик разойдётся */
    onEditorKeydown(e: KeyboardEvent) {
        if (!(e.ctrlKey || e.metaKey)) return;
        const key = e.key.toLowerCase();
        if (key === "z" && !e.shiftKey) {
            e.preventDefault();
            this.runHistoryCommand("undo");
        } else if ((key === "z" && e.shiftKey) || key === "y") {
            e.preventDefault();
            this.runHistoryCommand("redo");
        }
    }

    /** Положение курсора — числом символов от начала текста */
    getCaretOffset(): number {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || !this.editorRef) return 0;
        const range = selection.getRangeAt(0);
        if (!this.editorRef.contains(range.startContainer)) return 0;

        const before = range.cloneRange();
        before.selectNodeContents(this.editorRef);
        before.setEnd(range.startContainer, range.startOffset);
        return before.toString().length;
    }

    setCaretOffset(offset: number) {
        if (!this.editorRef) return;
        const walker = document.createTreeWalker(this.editorRef, NodeFilter.SHOW_TEXT, null as any);
        let left = offset;
        let node: any;
        while ((node = walker.nextNode())) {
            if (left <= node.data.length) {
                const range = document.createRange();
                range.setStart(node, left);
                range.collapse(true);
                const selection = window.getSelection();
                selection?.removeAllRanges();
                selection?.addRange(range);
                this.savedRange = range.cloneRange();
                return;
            }
            left -= node.data.length;
        }
    }

    updateActiveFormats() {
        this.$nextTick(() => {
            try {
                this.activeFormats.bold = document.queryCommandState("bold");
                this.activeFormats.italic = document.queryCommandState("italic");
                this.activeFormats.underline = document.queryCommandState("underline");
            } catch (e) {
                console.warn("Не удалось определить состояние форматирования", e);
            }
            this.syncSelectedBlock();
        });
    }

    // ── Отступы блока ─────────────────────────────────────────────────────────
    // Показываем отступы блока, в котором стоит курсор, и позволяем их менять.
    // Значения пишутся прямо в блок (inline-стилем), поэтому на сайте
    // получается ровно то же, что видно в редакторе.

    static readonly BLOCK_TAGS = ["P", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "UL", "OL", "BLOCKQUOTE", "TABLE", "DIV"];
    static readonly BLOCK_NAMES: any = {
        P: "Абзац", H1: "Заголовок 1", H2: "Заголовок 2", H3: "Заголовок 3",
        H4: "Заголовок 4", H5: "Заголовок 5", H6: "Заголовок 6",
        LI: "Пункт списка", UL: "Список", OL: "Нумерованный список",
        BLOCKQUOTE: "Цитата", TABLE: "Таблица", DIV: "Блок",
    };

    syncSelectedBlock() {
        const block = this.findSelectedBlock();
        if (!block) {
            // Курсор ушёл из текста — например, в поле отступов. Держим прежний
            // блок, иначе панель исчезала ровно в момент, когда в неё кликают.
            if (this.selectedBlockEl && this.editorRef?.contains(this.selectedBlockEl)) return;
            this.selectedBlockEl = null;
            this.selectedBlockLabel = "";
            return;
        }
        this.selectedBlockEl = block;
        this.selectedBlockLabel = HmtlInlineEditor.BLOCK_NAMES[block.tagName] ?? "Блок";
        const style = window.getComputedStyle(block);
        for (const side of this.spacingSides) {
            this.$set(this.blockSpacing, `margin${side.key}`, Math.round(parseFloat(style[`margin${side.key}`]) || 0));
            this.$set(this.blockSpacing, `padding${side.key}`, Math.round(parseFloat(style[`padding${side.key}`]) || 0));
        }
    }

    findSelectedBlock(): any {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || !this.editorRef) return null;
        let node: any = selection.getRangeAt(0).startContainer;
        if (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode;
        while (node && node !== this.editorRef) {
            if (HmtlInlineEditor.BLOCK_TAGS.includes(node.tagName)) return node;
            node = node.parentNode;
        }
        return null;
    }

    applyBlockSpacing() {
        const block = this.selectedBlockEl;
        if (!block || !this.editorRef?.contains(block)) return;
        for (const side of this.spacingSides) {
            const margin = Number(this.blockSpacing[`margin${side.key}`]);
            const padding = Number(this.blockSpacing[`padding${side.key}`]);
            block.style[`margin${side.key}`] = Number.isFinite(margin) ? `${margin}px` : "";
            block.style[`padding${side.key}`] = Number.isFinite(padding) ? `${padding}px` : "";
        }
        this.content = this.editorRef.innerHTML;
        this.scheduleHistory();
    }

    resetBlockSpacing() {
        const block = this.selectedBlockEl;
        if (!block || !this.editorRef?.contains(block)) return;
        for (const side of this.spacingSides) {
            block.style.removeProperty(`margin-${side.key.toLowerCase()}`);
            block.style.removeProperty(`padding-${side.key.toLowerCase()}`);
        }
        if (!block.getAttribute("style")) block.removeAttribute("style");
        this.syncContent();
        this.syncSelectedBlock();
    }

    // ── Предупреждения по тексту ──────────────────────────────────────────────
    get contentWarnings(): string[] {
        const warnings: string[] = [];
        const headings = (this.content.match(/<h1[\s>]/gi) || []).length;
        if (headings === 1) {
            warnings.push(
                "В тексте есть заголовок H1. Сайт выводит свой H1 из поля «Подпись» — на странице их станет два, это вредно для поиска."
            );
        } else if (headings > 1) {
            warnings.push(
                `В тексте ${headings} заголовка H1. На странице будет ещё и свой — оставьте один, остальные сделайте H2.`
            );
        }
        if (/<font[\s>]/i.test(this.content)) {
            warnings.push("В тексте остался старый тег <font> — размер шрифта может отличаться от сайта.");
        }
        if (/\sstyle="[^"]*font-size/i.test(this.content) && /<h[1-6][^>]*>[^<]*<[^>]*font-size/i.test(this.content)) {
            warnings.push("У заголовка задан свой размер шрифта — он перебивает оформление сайта.");
        }
        return warnings;
    }

    // ── Таблица и очистка форматирования ──────────────────────────────────────
    promptTable() {
        this.saveSelection();
        this.showTableModal = true;
    }

    insertTable() {
        const rows = Math.min(Math.max(Number(this.tableRows) || 1, 1), 30);
        const cols = Math.min(Math.max(Number(this.tableCols) || 1, 1), 10);
        const cell = (tag: string) => `<${tag}>&nbsp;</${tag}>`;
        const bodyRows: string[] = [];
        for (let i = 0; i < rows - (this.tableHasHeader ? 1 : 0); i++) {
            bodyRows.push(`<tr>${cell("td").repeat(cols)}</tr>`);
        }
        const head = this.tableHasHeader ? `<tr>${cell("th").repeat(cols)}</tr>` : "";
        const html = `<table>${head}${bodyRows.join("")}</table><p><br></p>`;

        this.showTableModal = false;
        this.$nextTick(() => {
            this.restoreSelection();
            insertHtmlAtCursor(html, this.editorRef);
            this.syncContent();
            this.savedRange = null;
        });
    }

    /** Снимает оформление с выделения: цвет, размер, жирный/курсив и прочее */
    clearFormatting() {
        this.restoreSelection();
        this.editorRef?.focus();
        document.execCommand("removeFormat");
        document.execCommand("unlink");
        this.syncContent();
        this.updateActiveFormats();
    }

    isFormatActive(format: string): boolean {
        return this.activeFormats[format] || false;
    }

    // === ЕДИНСТВЕННЫЙ метод сохранения выделения ===
    saveSelection() {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || !this.editorRef) {
            this.savedRange = null;
            return;
        }

        const range = selection.getRangeAt(0);
        if (!this.editorRef.contains(range.commonAncestorContainer)) {
            this.savedRange = null;
            return;
        }

        this.savedRange = range.cloneRange();
    }

    // === ЕДИНСТВЕННЫЙ метод восстановления выделения ===
    restoreSelection() {
        if (!this.savedRange || !this.editorRef) return;

        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(this.savedRange);
            this.editorRef.focus();
        }
    }

    // === ЦВЕТ ===
    saveSelectionForColor() {
        this.saveSelection();
    }

    applyColor(color: string) {
        this.currentColor = color;
        this.restoreSelection();
        document.execCommand("foreColor", false, color);
        this.syncContent();
        // Опционально: обновить savedRange, если нужно продолжить редактирование
        // Но обычно после применения — выделение остаётся
    }

    // === РАЗМЕР ШРИФТА ===
    saveSelectionForFontSize() {
        this.saveSelection();
    }

    applyFontSize(px: string) {
        this.selectedFontSize = px;
        this.restoreSelection();
        this.editorRef?.focus();
        if (!this.editorRef) return;

        // execCommand умеет только размеры 1–7, поэтому ставим служебный 7
        // и тут же заменяем его на нужные пиксели
        document.execCommand("styleWithCSS", false, "false");
        document.execCommand("fontSize", false, "7");
        this.editorRef.querySelectorAll('font[size="7"]').forEach((node: any) => {
            const span = document.createElement("span");
            span.style.fontSize = px;
            while (node.firstChild) span.appendChild(node.firstChild);
            node.parentNode?.replaceChild(span, node);
        });

        this.syncContent();
        this.updateActiveFormats();
    }

    // === ССЫЛКА ===
    promptLink() {
        this.saveSelection(); // Используем общий метод!
        const selection = window.getSelection();
        this.savedSelectionText = selection ? selection.toString() : "";
        this.linkUrl = "";
        this.linkText = this.savedSelectionText || "";
        this.showLinkModal = true;
    }

    insertLink() {
        if (!this.linkUrl || !this.dialog) return;

        const displayText = this.linkText || this.savedSelectionText || "ссылка";
        const html = `<a href="${this.linkUrl}" target="_blank">${displayText}</a>`;

        this.showLinkModal = false;

        this.$nextTick(() => {
            this.restoreSelection(); // Единый метод
            document.execCommand("insertHTML", false, html);
            this.syncContent();
            this.updateActiveFormats();
            this.savedRange = null; // Сбрасываем после вставки
        });
    }

    // === ИЗОБРАЖЕНИЕ ===
    promptImage() {
        this.saveSelection(); // Единый метод
        this.imageUrl = "";
        this.imageAlt = "";
        this.showImageModal = true;
    }

    insertImage() {
        if (!this.imageUrl || !this.dialog) return;

        const alt = this.imageAlt || "Изображение";
        const html = `<img src="${this.imageUrl}" alt="${alt}">`;

        this.showImageModal = false;

        this.$nextTick(() => {
            this.restoreSelection(); // Единый метод
            insertHtmlAtCursor(html, this.editorRef);
            this.syncContent();
            this.updateActiveFormats();
            this.savedRange = null;
        });
    }

    async saveChanges() {
        this.loading = true;
        try {
            if (this.mode === 1) {
                this.content = this.htmlValue;
            }
            this.value = this.format(this.content);
            await this.save();
        } catch (e) {
            console.error(e);
        } finally {
            this.loading = false;
            this.dialog = false;
        }
    }

    async discartChanges() {
        // Закрытие крестиком теряло правки молча — спрашиваем, если текст менялся
        if (this.format(this.content) !== this.openedWith) {
            const confirmed = await this.$confirm({
                title: "Есть несохранённые изменения",
                text: "Закрыть редактор и потерять правки?",
            });
            if (!confirmed) return;
        }
        this.cancelChanges();
        this.dialog = false;
    }

    // cleanWordGarbage(html) {
    //     if (!html) return "<p><br></p>";

    //     const isWord = /mso-|liberation|sans'|class=["'][^"']*doc[^"']*|xmlns:o=|<o:p>|<v:|<!\[if gte mso|style=["'][^"']*mso-/.test(html);

    //     if (!isWord) {
    //         return html;
    //     }

    //     const parser = new DOMParser();
    //     const doc = parser.parseFromString(html, "text/html");

    //     const allowedTags = new Set([
    //         "p", "h1", "h2", "h3", "h4", "h5", "h6",
    //         "ol", "ul", "li", "br", "a", "b", "strong", "i", "em"
    //     ]);

    //     const unwrapTags = ["span", "font", "o:p", "st1", "mark", "u", "strike", "div"];

    //     const cleanNode = (node) => {
    //         if (node.nodeType === Node.ELEMENT_NODE) {
    //             const tagName = node.tagName.toLowerCase();

    //             if (tagName === "a") {
    //                 const href = node.getAttribute("href");
    //                 while (node.attributes.length > 0) {
    //                     node.removeAttribute(node.attributes[0].name);
    //                 }
    //                 if (href) node.setAttribute("href", href);
    //             } else {
    //                 while (node.attributes.length > 0) {
    //                     node.removeAttribute(node.attributes[0].name);
    //                 }
    //             }

    //             if (unwrapTags.includes(tagName)) {
    //                 const parent = node.parentNode;
    //                 while (node.firstChild) {
    //                     parent.insertBefore(node.firstChild, node);
    //                 }
    //                 parent.removeChild(node);
    //                 return;
    //             }

    //             if (!allowedTags.has(tagName)) {
    //                 const p = doc.createElement("p");
    //                 while (node.firstChild) {
    //                     p.appendChild(node.firstChild);
    //                 }
    //                 node.parentNode.replaceChild(p, node);
    //                 node = p;
    //             }

    //             for (let i = 0; i < node.childNodes.length; i++) {
    //                 cleanNode(node.childNodes[i]);
    //             }
    //         }
    //     };

    //     Array.from(doc.body.childNodes).forEach(cleanNode);

    //     let cleaned = new XMLSerializer().serializeToString(doc.body)
    //         .replace(/^<body>|<\/body>$/gi, "")
    //         .replace(/&nbsp;/g, " ")
    //         .replace(/\s+/g, " ")
    //         .replace(/>\s+</g, "><");

    //     cleaned = cleaned
    //         .replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, "$1")
    //         .replace(/<p>\s*<\/p>/g, "<p><br></p>")
    //         .replace(/<p><p([^>]*)>/g, "<p$1>")
    //         .replace(/<\/p><\/p>/g, "</p>")
    //         .replace(/<div><br><\/div>/g, "<p><br></p>");

    //     return cleaned.trim() || "<p><br></p>";
    // }

    // format(html: string) {
    //     if (!html) return "<p><br></p>";

    //     try {
    //         // 1. Чистка от Word-мусора (внутри cleanWordGarbage уже удаляются атрибуты, но не style)
    //         html = this.cleanWordGarbage(html);

    //         // 2. Заменяем <font> на <span style="..."> ДО удаления стилей
    //         html = html.replace(/<font[^>]*color\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, color, content) => {
    //             return `<span style="color: ${color}">${content}</span>`;
    //         });

    //         html = html.replace(/<font[^>]*size\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, size, content) => {
    //             const sizes: Record<string, string> = {
    //                 "1": "8px", "2": "10px", "3": "12px", "4": "14px",
    //                 "5": "18px", "6": "24px", "7": "36px"
    //             };
    //             const fontSize = sizes[size] || "14px";
    //             return `<span style="font-size: ${fontSize}">${content}</span>`;
    //         });

    //         // 3. Удаляем ЛИШЬ нежелательные стили, но сохраняем color и font-size
    //         // Ищем все теги со style и фильтруем содержимое атрибута
    //         html = html.replace(/<([^>]+?)\sstyle\s*=\s*["']([^"']*)["']([^>]*)>/gi, (match, before, styleAttr, after) => {
    //             // Разбираем style="..."
    //             const styles = styleAttr.split(';').map(s => s.trim()).filter(s => s);
    //             const keptStyles: string[] = [];

    //             for (const decl of styles) {
    //                 const [prop, val] = decl.split(':').map(p => p.trim().toLowerCase());
    //                 // Сохраняем ТОЛЬКО нужные свойства
    //                 if (prop === 'color' || prop === 'font-size') {
    //                     keptStyles.push(`${prop}: ${val}`);
    //                 }
    //             }

    //             if (keptStyles.length > 0) {
    //                 return `<${before} style="${keptStyles.join('; ')}"${after}>`;
    //             } else {
    //                 // Если не осталось стилей — удаляем атрибут style полностью
    //                 return `<${before}${after}>`;
    //             }
    //         });

    //         // 4. Остальная чистка (без изменения)
    //         html = html.replace(/&nbsp;/g, " ");
    //         html = html.replace(/<div(?![^>]*class=)([^>]*)>([\s\S]*?)<\/div>/g, (match, p1, p2) => {
    //             return `<p${p1}>${p2}</p>`;
    //         });
    //         html = html.replace(/<div><br><\/div>/g, "<p><br></p>");
    //         html = html
    //             .replace(/<p>\s*<ol>/g, "<ol>")
    //             .replace(/<p>\s*<ul>/g, "<ul>")
    //             .replace(/<\/ol>\s*<\/p>/g, "</ol>")
    //             .replace(/<\/ul>\s*<\/p>/g, "</ul>");
    //         html = html.replace(/<p><\/p>/g, "<p><br></p>");
    //         html = html
    //             .replace(/<p><p([^>]*)>/g, "<p$1>")
    //             .replace(/<\/p><\/p>/g, "</p>");
    //         html = html
    //             .replace(/^<body[^>]*>|<\/body>$/gi, "")
    //             .replace(/^<html[^>]*>|<\/html>$/gi, "")
    //             .replace(/^<!DOCTYPE[^>]*>/i, "");

    //         return beautify.html(html, {
    //             indent_size: 2,
    //             wrap_attributes: "force-aligned",
    //             end_with_newline: false,
    //             unformatted: ["code", "pre", "textarea"],
    //         });
    //     } catch (e) {
    //         console.error("Ошибка форматирования HTML", e);
    //         return html;
    //     }
    // }

    cleanWordGarbage(html) {
        if (!html) return "<p><br></p>";

        // ВАЖНО: не детектим Word по подстроке "doc" в class — она ложно срабатывает
        // на легитимные классы вёрстки (doctor-price, doctor-detail__* → содержат "doc"),
        // из-за чего чистилка сносила div/span и все атрибуты (class), ломая вставленный HTML.
        const isWord = /mso-|xmlns:o=|<o:p>|<v:|<!\[if gte mso|style=["'][^"']*mso-|class=["']Mso/.test(html);

        if (!isWord) {
            return html;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const allowedTags = new Set([
            "p", "h1", "h2", "h3", "h4", "h5", "h6",
            "ol", "ul", "li", "br", "a", "b", "strong", "i", "em"
        ]);

        const unwrapTags = ["span", "font", "o:p", "st1", "mark", "u", "strike", "div"];

        const cleanNode = (node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();

                if (tagName === "a") {
                    const href = node.getAttribute("href");
                    while (node.attributes.length > 0) {
                        node.removeAttribute(node.attributes[0].name);
                    }
                    if (href) node.setAttribute("href", href);
                } else {
                    while (node.attributes.length > 0) {
                        node.removeAttribute(node.attributes[0].name);
                    }
                }

                if (unwrapTags.includes(tagName)) {
                    const parent = node.parentNode;
                    while (node.firstChild) {
                        parent.insertBefore(node.firstChild, node);
                    }
                    parent.removeChild(node);
                    return;
                }

                if (!allowedTags.has(tagName)) {
                    const p = doc.createElement("p");
                    while (node.firstChild) {
                        p.appendChild(node.firstChild);
                    }
                    node.parentNode.replaceChild(p, node);
                    node = p;
                }

                for (let i = 0; i < node.childNodes.length; i++) {
                    cleanNode(node.childNodes[i]);
                }
            }
        };

        Array.from(doc.body.childNodes).forEach(cleanNode);

        let cleaned = new XMLSerializer().serializeToString(doc.body)
            .replace(/^<body>|<\/body>$/gi, "")
            .replace(/&nbsp;/g, " ")
            .replace(/\s+/g, " ")
            .replace(/>\s+</g, "><");

        cleaned = cleaned
            .replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, "$1")
            .replace(/<p>\s*<\/p>/g, "<p><br></p>")
            .replace(/<p><p([^>]*)>/g, "<p$1>")
            .replace(/<\/p><\/p>/g, "</p>")
            .replace(/<div><br><\/div>/g, "<p><br></p>");

        return cleaned.trim() || "<p><br></p>";
    }

    format(html: string) {
        if (!html) return "<p><br></p>";

        try {
            // 1. Чистка от Word-мусора
            html = this.cleanWordGarbage(html);

            // 2. Заменяем <font> на <span style="..."> ДО удаления стилей
            html = html.replace(/<font[^>]*color\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, color, content) => {
                return `<span style="color: ${color}">${content}</span>`;
            });

            html = html.replace(/<font[^>]*size\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, size, content) => {
                const sizes: Record<string, string> = {
                    "1": "8px", "2": "10px", "3": "12px", "4": "14px",
                    "5": "18px", "6": "24px", "7": "36px"
                };
                const fontSize = sizes[size] || "14px";
                return `<span style="font-size: ${fontSize}">${content}</span>`;
            });

            // 3. Удаляем ЛИШЬ нежелательные стили, но сохраняем цвет, размер шрифта
            // и выравнивание. text-align обязателен: кнопки «по центру / по ширине»
            // ставят именно его, и без него выравнивание молча терялось при сохранении.
            html = html.replace(/<([^>]+?)\sstyle\s*=\s*["']([^"']*)["']([^>]*)>/gi, (match, before, styleAttr, after) => {
                const styles = styleAttr.split(';').map(s => s.trim()).filter(s => s);
                const keptStyles: string[] = [];

                for (const decl of styles) {
                    const [prop, val] = decl.split(':').map(p => p.trim().toLowerCase());
                    // margin/padding — отступы, заданные в панели отступов блока
                    if (prop === 'color' || prop === 'font-size' || prop === 'text-align' ||
                        /^(margin|padding)(-(top|right|bottom|left))?$/.test(prop)) {
                        keptStyles.push(`${prop}: ${val}`);
                    }
                }

                if (keptStyles.length > 0) {
                    return `<${before} style="${keptStyles.join('; ')}"${after}>`;
                } else {
                    return `<${before}${after}>`;
                }
            });

            // 4. Удаляем пустые спаны (без атрибутов) внутри параграфов.
            // Атрибуты самого абзаца сохраняем: раньше они стирались вместе со спанами,
            // и выравнивание (text-align) исчезало при каждом сохранении.
            html = html.replace(/<p([^>]*)>(.*?)<\/p>/gi, (match, pAttrs, pContent) => {
                // Удаляем спаны без атрибутов внутри параграфа
                const cleanedContent = pContent.replace(/<span[^>]*>(.*?)<\/span>/gi, (spanMatch, spanContent, offset) => {
                    // Проверяем, есть ли у спана какие-либо атрибуты
                    const spanTag = spanMatch.substring(0, spanMatch.indexOf('>'));
                    const hasAttributes = spanTag.includes(' ') && (spanTag.includes('=') || spanTag.includes('style'));

                    if (!hasAttributes) {
                        return spanContent; // Удаляем span, оставляем только содержимое
                    }
                    return spanMatch; // Оставляем span с атрибутами
                });

                return `<p${pAttrs}>${cleanedContent}</p>`;
            });

            // 5. Остальная чистка (без изменения)
            html = html.replace(/&nbsp;/g, " ");
            // div без класса → абзац. Идём от самых вложенных (внутри нет других div):
            // одна общая регулярка рвала вложенность — <div><div>т</div></div> превращался
            // в <p><div>т</p></div>, и разметка ломалась.
            const innerDiv = /<div(?![^>]*class=)([^>]*)>((?:(?!<\/?div)[\s\S])*?)<\/div>/g;
            for (let pass = 0; pass < 10 && innerDiv.test(html); pass++) {
                innerDiv.lastIndex = 0;
                html = html.replace(innerDiv, (match, p1, p2) => `<p${p1}>${p2}</p>`);
            }
            html = html
                .replace(/<p>\s*<ol>/g, "<ol>")
                .replace(/<p>\s*<ul>/g, "<ul>")
                .replace(/<\/ol>\s*<\/p>/g, "</ol>")
                .replace(/<\/ul>\s*<\/p>/g, "</ul>");
            html = html
                .replace(/<p><p([^>]*)>/g, "<p$1>")
                .replace(/<\/p><\/p>/g, "</p>");
            // Пустые абзацы-распорки убираем, а не воссоздаём. В редакторе такой абзац —
            // незаметная пустая строка (22 px), на сайте — дыра ~59 px, и раньше он
            // возвращался при каждом сохранении: <p></p> снова становился <p><br></p>.
            // Перечень тегов закрыт по границе (\s или >), иначе <img> попадал бы
            // под шаблон курсива <i> и абзац с фотографией считался пустым.
            html = html.replace(
                /<p[^>]*>(?:\s|<br\s*\/?>|<\/?(?:b|strong|i|em|u|span|font)(?:\s[^>]*)?>)*<\/p>/gi,
                ""
            );
            html = html
                .replace(/^<body[^>]*>|<\/body>$/gi, "")
                .replace(/^<html[^>]*>|<\/html>$/gi, "")
                .replace(/^<!DOCTYPE[^>]*>/i, "");

            if (!html.trim()) return "<p><br></p>";

            return beautify.html(html, {
                indent_size: 2,
                wrap_attributes: "force-aligned",
                end_with_newline: false,
                unformatted: ["code", "pre", "textarea"],
            });
        } catch (e) {
            console.error("Ошибка форматирования HTML", e);
            return html;
        }
    }
}
